import { generateEcdhKeyPair, exportPublicKey, deriveSessionMaterials } from './session-crypto';
import type { CryptoMaterials } from './session-crypto';

export interface TransportContext
{
    /** Canonical API environment/base URL chosen by the adapter, not by a request payload. */
    readonly apiEnvironment: string;
    readonly identity: string;
    readonly sessionId: string;
    readonly generation: number;
    /** Null until key setup returns a server-issued version. Never use a local placeholder. */
    readonly transportVersion: string | null;
    /** Absolute milliseconds; null for a provider that exposes no deadline. */
    readonly expiresAt: number | null;
}

export interface KeyExchangeAttempt
{
    readonly cancelled: Promise<void>;
    isCurrent(): boolean;
}

export interface KeyExchangeResponse
{
    readonly public_key: string;
    /** Adapter-normalized API metadata. Never manufacture this from the session token's expiry. */
    readonly transportVersion: string;
}

export interface VersionedCryptoMaterials extends CryptoMaterials
{
    readonly transportVersion: string;
}

export interface SessionMaterialCacheOptions<C extends TransportContext>
{
    subtle: SubtleCrypto;
    clock: { now(): number; };
    /** Pure, synchronous snapshot including the pending session candidate; provider fields must be immutable. */
    currentContext(): C | null;
    /** Use the captured context for destination and credentials. Cancellation is cooperative; no retries here. */
    exchange(context: C, request: { public_key: string; }, attempt: KeyExchangeAttempt): KeyExchangeResponse | Promise<KeyExchangeResponse>;
}

export type MaterialErrorCode = 'no-session' | 'invalid-context' | 'context-changed' | 'expired' | 'cancelled' | 'exchange-failed' | 'crypto-failed';
export type MaterialError = Error & { readonly code: MaterialErrorCode; readonly cause?: unknown; };

function materialError(code: MaterialErrorCode, cause?: unknown): MaterialError
{
    return Object.assign(new Error(`Session materials: ${code}`), { code, cause });
}

interface Entry<C>
{
    context: C;
    lifetime?: KeyExchangeAttempt;
    attempt: KeyExchangeAttempt;
    cancel(): void;
    promise: Promise<() => void>;
    resolve(activate: () => void): void;
    reject(error: MaterialError): void;
    error: MaterialError | null;
    materials: CryptoMaterials | null;
    acceptedVersion: string | null;
    active: boolean;
}

/** One cache per client/sidebar, holding only its current context. No HTTP dispatch or recovery policy. */
export class SessionMaterialCache<C extends TransportContext>
{
    private readonly options: SessionMaterialCacheOptions<C>;
    private entry: Entry<C> | null = null;

    public constructor(options: SessionMaterialCacheOptions<C>) { this.options = options; }

    /**
     * Coalesce generation/export/exchange/derivation and return a guarded synchronous activation.
     * An optional session lifetime is structurally compatible with common/session's SessionAttempt.
     * Pass the lifetime of the session generation, not an individual HTTP request.
     */
    public prepare(lifetime?: KeyExchangeAttempt): Promise<() => void>
    {
        try
        {
            if (lifetime && !lifetime.isCurrent()) return Promise.reject(materialError('cancelled'));
            if (this.entry)
            {
                const entry = this.entry;
                const error = this.check(entry);
                if (!error) return entry.promise;
                // A newly observed context may start its own exchange; expiry/cancellation cannot retry itself.
                if (error.code !== 'context-changed') return Promise.reject(error);
            }
            const context = this.readContext();
            if (!context) return Promise.reject(materialError('no-session'));
            if (this.expired(context)) return Promise.reject(materialError('expired'));
            let resolve!: Entry<C>['resolve'];
            let reject!: Entry<C>['reject'];
            let cancel!: () => void;
            const promise = new Promise<() => void>((yes, no) => { resolve = yes; reject = no; });
            const cancelled = new Promise<void>(done => { cancel = done; });
            const entry: Entry<C> = {
                context, lifetime, promise, resolve, reject, cancel,
                attempt: { cancelled, isCurrent: () => this.check(entry) === null },
                error: null, materials: null, acceptedVersion: null, active: false,
            };
            this.entry = entry;
            if (lifetime)
            {
                void lifetime.cancelled.then(
                    () => this.invalidate(entry, materialError('cancelled')),
                    cause => this.invalidate(entry, materialError('cancelled', cause)),
                );
            }
            this.run(entry);
            return promise;
        }
        catch (cause)
        {
            const error = materialError('invalid-context', cause);
            if (this.entry) this.invalidate(this.entry, error);
            return Promise.reject(error);
        }
    }

    /** Read only activated, current materials. Recheck after caller-owned asynchronous work. */
    public getMaterials(): VersionedCryptoMaterials | null
    {
        const entry = this.entry;
        if (!entry || this.check(entry) || !entry.active || !entry.materials || !entry.acceptedVersion) return null;
        // Do not let a consumer mutate the cached AES counter through the returned buffer.
        return { key: entry.materials.key, counter: entry.materials.counter.slice(0), transportVersion: entry.acceptedVersion };
    }

    /** Logout/context notifications must call reset even when there are no requests to observe the change. */
    public reset(): void
    {
        if (this.entry) this.invalidate(this.entry, materialError('cancelled'));
    }

    private run(entry: Entry<C>): void
    {
        let stage: MaterialErrorCode = 'crypto-failed';
        void Promise.resolve().then(() => {
            this.requireCurrent(entry);
            return generateEcdhKeyPair(this.options.subtle);
        }).then(pair => {
            this.requireCurrent(entry);
            return exportPublicKey(pair.publicKey, this.options.subtle).then(publicKey => ({ pair, publicKey }));
        }).then(({ pair, publicKey }) => {
            this.requireCurrent(entry);
            stage = 'exchange-failed';
            return Promise.resolve(this.options.exchange(entry.context, { public_key: publicKey }, entry.attempt)).then(response => {
                this.requireCurrent(entry);
                if (!response || typeof response.public_key !== 'string' || !response.public_key
                    || typeof response.transportVersion !== 'string' || !response.transportVersion.trim())
                {
                    throw new Error('Key exchange returned invalid metadata');
                }
                if (entry.context.transportVersion !== null && response.transportVersion !== entry.context.transportVersion)
                {
                    const error = materialError('context-changed');
                    this.invalidate(entry, error);
                    throw error;
                }
                // Adoption belongs to this guarded exchange only. It cannot update the host's
                // session or escape the derivation/activation fences below.
                entry.acceptedVersion = response.transportVersion;
                stage = 'crypto-failed';
                return deriveSessionMaterials(pair.privateKey, response.public_key, this.options.subtle);
            });
        }).then(materials => {
            this.requireCurrent(entry);
            entry.materials = materials;
            entry.resolve(() => {
                this.requireCurrent(entry);
                entry.active = true;
            });
        }).catch(cause => {
            // An old failure must not clear or reject a newer generation's entry.
            if (!this.check(entry)) this.invalidate(entry, materialError(stage, cause));
        });
    }

    private requireCurrent(entry: Entry<C>): void
    {
        const error = this.check(entry);
        if (error) throw error;
    }

    private check(entry: Entry<C>): MaterialError | null
    {
        if (entry.error) return entry.error;
        let error: MaterialError | null = null;
        try
        {
            const current = this.readContext();
            if (this.entry !== entry) error = materialError('context-changed');
            else if (entry.lifetime && !entry.lifetime.isCurrent()) error = materialError('cancelled');
            else if (!current) error = materialError('no-session');
            else if (!this.sameScope(entry, current)) error = materialError('context-changed');
            else if (this.expired(entry.context) || this.expired(current)) error = materialError('expired');
            else if (entry.context.transportVersion === null && current.transportVersion !== null)
            {
                // Once the host publishes the accepted version, losing it is a context change.
                // Preserve the originally captured deadline and credentials while pinning it.
                entry.context = Object.freeze(Object.assign({}, entry.context, { transportVersion: current.transportVersion }));
            }
        }
        catch (cause) { error = materialError('invalid-context', cause); }
        if (error) this.invalidate(entry, error);
        return error;
    }

    private invalidate(entry: Entry<C>, error: MaterialError): void
    {
        if (entry.error) return;
        entry.error = error;
        entry.materials = null;
        entry.acceptedVersion = null;
        entry.active = false;
        if (this.entry === entry) this.entry = null;
        entry.cancel();
        entry.reject(error);
    }

    private readContext(): C | null
    {
        const value = this.options.currentContext();
        if (!value) return null;
        if (![value.apiEnvironment, value.identity, value.sessionId].every(part => typeof part === 'string' && part.length > 0)
            || (value.transportVersion !== null && (typeof value.transportVersion !== 'string' || !value.transportVersion.trim()))
            || !Number.isSafeInteger(value.generation) || value.generation < 0
            || (value.expiresAt !== null && !Number.isFinite(value.expiresAt)))
        {
            throw new Error('Session transport context is incomplete');
        }
        return Object.freeze(Object.assign({}, value));
    }

    private sameScope(entry: Entry<C>, right: C): boolean
    {
        const left = entry.context;
        return left.apiEnvironment === right.apiEnvironment && left.identity === right.identity
            && left.sessionId === right.sessionId && left.generation === right.generation
            && (left.transportVersion === right.transportVersion
                || (left.transportVersion === null && entry.acceptedVersion !== null && entry.acceptedVersion === right.transportVersion));
    }

    private expired(context: C): boolean
    {
        return context.expiresAt !== null && !(this.options.clock.now() < context.expiresAt);
    }
}
